import React, { useContext, useState, useEffect } from 'react';
import { LanguageContext } from '../App';
import Layout from './Layout';
import axios from 'axios';

const TaskManager = () => {
  const { t, tasks, setTasks, fetchTasks, API } = useContext(LanguageContext);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [filters, setFilters] = useState({
    category: '',
    status: '',
    priority: ''
  });

  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    category: 'other',
    priority: 'medium',
    due_date: ''
  });

  const categories = ['meeting', 'followup', 'directive', 'email', 'note', 'other'];
  const priorities = ['high', 'medium', 'low'];
  const statuses = ['pending', 'in_progress', 'completed', 'cancelled'];

  const resetForm = () => {
    setTaskForm({
      title: '',
      description: '',
      category: 'other',
      priority: 'medium',
      due_date: ''
    });
    setEditingTask(null);
    setShowTaskForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTask) {
        // Update existing task
        await axios.put(`${API}/tasks/${editingTask.id}`, taskForm);
      } else {
        // Create new task
        await axios.post(`${API}/tasks`, taskForm);
      }
      await fetchTasks();
      resetForm();
    } catch (error) {
      console.error('Error saving task:', error);
      alert('خطأ في حفظ المهمة');
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setTaskForm({
      title: task.title,
      description: task.description || '',
      category: task.category,
      priority: task.priority,
      due_date: task.due_date ? task.due_date.split('T')[0] : ''
    });
    setShowTaskForm(true);
  };

  const handleDelete = async (taskId) => {
    if (window.confirm('هل أنت متأكد من حذف هذه المهمة؟')) {
      try {
        await axios.delete(`${API}/tasks/${taskId}`);
        await fetchTasks();
      } catch (error) {
        console.error('Error deleting task:', error);
        alert('خطأ في حذف المهمة');
      }
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await axios.put(`${API}/tasks/${taskId}`, { status: newStatus });
      await fetchTasks();
    } catch (error) {
      console.error('Error updating task status:', error);
    }
  };

  const filteredTasks = tasks.filter(task => {
    return (!filters.category || task.category === filters.category) &&
           (!filters.status || task.status === filters.status) &&
           (!filters.priority || task.priority === filters.priority);
  });

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high': return '🔴';
      case 'medium': return '🟡';
      case 'low': return '🟢';
      default: return '⚪';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'meeting': return '👥';
      case 'followup': return '📞';
      case 'directive': return '📋';
      case 'email': return '📧';
      case 'note': return '📝';
      case 'other': return '📌';
      default: return '📌';
    }
  };

  return (
    <Layout>
      <div className="fade-in">
        {/* Page Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {t('tasks')}
            </h1>
            <p className="text-gray-600">
              إدارة وتتبع جميع مهامك اليومية والأسبوعية
            </p>
          </div>
          <button
            onClick={() => setShowTaskForm(true)}
            className="btn btn-primary"
          >
            ➕ {t('newTask')}
          </button>
        </div>

        {/* Filters */}
        <div className="card mb-6">
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="form-group">
                <label className="form-label">الفئة</label>
                <select
                  className="form-control form-select"
                  value={filters.category}
                  onChange={(e) => setFilters({...filters, category: e.target.value})}
                >
                  <option value="">جميع الفئات</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>
                      {getCategoryIcon(cat)} {t(cat)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">الحالة</label>
                <select
                  className="form-control form-select"
                  value={filters.status}
                  onChange={(e) => setFilters({...filters, status: e.target.value})}
                >
                  <option value="">جميع الحالات</option>
                  {statuses.map(status => (
                    <option key={status} value={status}>
                      {t(status)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">الأولوية</label>
                <select
                  className="form-control form-select"
                  value={filters.priority}
                  onChange={(e) => setFilters({...filters, priority: e.target.value})}
                >
                  <option value="">جميع الأولويات</option>
                  {priorities.map(priority => (
                    <option key={priority} value={priority}>
                      {getPriorityIcon(priority)} {t(priority)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Task Form Modal */}
        {showTaskForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 max-h-screen overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">
                  {editingTask ? 'تعديل المهمة' : t('newTask')}
                </h3>
                <button
                  onClick={resetForm}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label">{t('taskTitle')}</label>
                  <input
                    type="text"
                    className="form-control"
                    value={taskForm.title}
                    onChange={(e) => setTaskForm({...taskForm, title: e.target.value})}
                    required
                    placeholder="أدخل عنوان المهمة..."
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">{t('taskDescription')}</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={taskForm.description}
                    onChange={(e) => setTaskForm({...taskForm, description: e.target.value})}
                    placeholder="تفاصيل إضافية عن المهمة (اختياري)..."
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">{t('category')}</label>
                  <select
                    className="form-control form-select"
                    value={taskForm.category}
                    onChange={(e) => setTaskForm({...taskForm, category: e.target.value})}
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>
                        {getCategoryIcon(cat)} {t(cat)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">{t('priority')}</label>
                  <select
                    className="form-control form-select"
                    value={taskForm.priority}
                    onChange={(e) => setTaskForm({...taskForm, priority: e.target.value})}
                  >
                    {priorities.map(priority => (
                      <option key={priority} value={priority}>
                        {getPriorityIcon(priority)} {t(priority)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">{t('dueDate')}</label>
                  <input
                    type="date"
                    className="form-control"
                    value={taskForm.due_date}
                    onChange={(e) => setTaskForm({...taskForm, due_date: e.target.value})}
                  />
                </div>

                <div className="flex gap-3 mt-6">
                  <button type="submit" className="btn btn-primary flex-1">
                    {editingTask ? 'تحديث المهمة' : 'حفظ المهمة'}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="btn btn-outline"
                  >
                    {t('cancel')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Tasks List */}
        <div className="space-y-4">
          {filteredTasks.length === 0 ? (
            <div className="card">
              <div className="card-body text-center py-12">
                <div className="text-6xl mb-4">📋</div>
                <h3 className="text-xl font-semibold mb-2">
                  {tasks.length === 0 ? 'لا توجد مهام' : 'لا توجد مهام تطابق المرشحات'}
                </h3>
                <p className="text-gray-600 mb-4">
                  {tasks.length === 0 
                    ? 'ابدأ بإضافة مهامك اليومية لتنظيم عملك' 
                    : 'جرب تغيير المرشحات لعرض مهام أخرى'
                  }
                </p>
                {tasks.length === 0 && (
                  <button
                    onClick={() => setShowTaskForm(true)}
                    className="btn btn-primary"
                  >
                    إضافة أول مهمة
                  </button>
                )}
              </div>
            </div>
          ) : (
            filteredTasks.map((task) => (
              <div key={task.id} className="task-item">
                <div className="task-header">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">{getCategoryIcon(task.category)}</span>
                      <h3 className="task-title">{task.title}</h3>
                    </div>
                    {task.description && (
                      <p className="task-description">{task.description}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(task)}
                      className="btn btn-outline btn-sm"
                      title="تعديل"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDelete(task.id)}
                      className="btn btn-outline btn-sm text-red-600 hover:bg-red-50"
                      title="حذف"
                    >
                      🗑️
                    </button>
                  </div>
                </div>

                <div className="task-meta">
                  <span className="task-badge badge-category">
                    {t(task.category)}
                  </span>
                  <span className={`task-badge badge-priority-${task.priority}`}>
                    {getPriorityIcon(task.priority)} {t(task.priority)}
                  </span>
                  
                  <select
                    value={task.status}
                    onChange={(e) => handleStatusChange(task.id, e.target.value)}
                    className={`task-badge badge-status-${task.status} border-none bg-transparent cursor-pointer`}
                  >
                    {statuses.map(status => (
                      <option key={status} value={status}>
                        {t(status)}
                      </option>
                    ))}
                  </select>

                  {task.due_date && (
                    <span className="text-sm text-gray-500 flex items-center gap-1">
                      📅 {new Date(task.due_date).toLocaleDateString('ar-SA')}
                    </span>
                  )}

                  <span className="text-xs text-gray-400">
                    تم الإنشاء: {new Date(task.created_at).toLocaleDateString('ar-SA')}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Task Summary */}
        {filteredTasks.length > 0 && (
          <div className="mt-6 text-center text-gray-600">
            عرض {filteredTasks.length} من {tasks.length} مهمة
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TaskManager;